import { DateInput } from "./DateInput";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, MAX_UPLOAD_MB } from "../lib/api";

import { tr, lbl } from "../lib/i18n";
export const DOC_TYPES = ["Registration", "Insurance", "ID", "Agreement", "Inspection Report", "Other"];
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,image/*,application/pdf";
const field = "w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm";

/** Upload a document to a vehicle (camera or files on a phone). */
export const DocumentUploadModal = ({ vehicleId, onClose, onSaved }) => {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ vehicle_id: vehicleId || "", doc_type: "Registration", name: "", expiry_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!vehicleId) api.get("/vehicles", { params: { limit: 200 } }).then(({ data }) => setVehicles(data.items || [])).catch(() => {});
  }, [vehicleId]);

  const save = async () => {
    if (!form.vehicle_id) { toast.error(tr("x.choose_a_vehicle")); return; }
    if (!file) { toast.error(tr("x.choose_a_file")); return; }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) { toast.error(tr("x.file_is_too_large_the_maximum_size_is_mb", { p0: MAX_UPLOAD_MB })); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      const { data } = await api.post(`/vehicles/${form.vehicle_id}/documents`, {
        url: up.url, name: form.name.trim() || up.name, doc_type: form.doc_type, expiry_date: form.expiry_date || null,
      });
      toast.success(tr("x.document_uploaded"));
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="panel p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="doc-upload-modal">
        <div className="eyebrow mb-3">{tr("x.upload_document")}</div>
        <div className="space-y-3">
          {!vehicleId && (
            <select className={field} value={form.vehicle_id} onChange={set("vehicle_id")} data-testid="doc-vehicle">
              <option value="">{tr("x.vehicle")}</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.stock_number} · {v.make} {v.model} {v.model_year}</option>)}
            </select>
          )}
          <select className={field} value={form.doc_type} onChange={set("doc_type")} data-testid="doc-type">
            {DOC_TYPES.map((t) => <option key={t} value={t}>{lbl(t)}</option>)}
          </select>
          <input className={field} placeholder={tr("x.name_optional")} value={form.name} onChange={set("name")} data-testid="doc-name" />
          <label className="text-xs opacity-70 block">{tr("x.expiry_date_optional")}
            <DateInput className={field} type="date" value={form.expiry_date} onChange={set("expiry_date")} data-testid="doc-expiry" />
          </label>
          <label className="btn-secondary inline-flex items-center gap-2" style={{ cursor: "pointer", width: "fit-content" }} data-testid="doc-file-label">
            {tr("x.choose_a_file")}
            <input type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ display: "none" }} data-testid="doc-file" />
          </label>
          <div className="text-xs opacity-70" dir="auto" data-testid="doc-file-name">{file ? file.name : tr("x.no_file_chosen")}</div>
          <div className="text-xs opacity-60">{tr("x.pdf_word_excel_or_image_up_to")} {MAX_UPLOAD_MB} {tr("x.mb")}</div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-secondary" data-testid="doc-cancel">{tr("x.cancel")}</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="doc-submit">{busy ? tr("x.uploading") : tr("x.upload")}</button>
        </div>
      </div>
    </div>
  );
};
